import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from "@angular/core";
import {forkJoin, Subject} from "rxjs";
import {Device, Site} from "../../../../domain/cloud-topology";
import {faPlusCircle, faTimesCircle} from "@fortawesome/free-solid-svg-icons";
import {SiteDeviceService} from "../../../../services/site/site-device.service";
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {FUELCELL} from "../../../../domain/constants";
import {MatSnackBar} from "@angular/material/snack-bar";

@Component({
    selector: 'app-site-other-devices',
    templateUrl: './site-other-devices.component.html',
    styleUrls: ['./site-other-devices.component.scss'],
    standalone: false
})
export class SiteOtherDevicesComponent implements OnChanges, OnDestroy {

  @Input() site: Site;
  @Output() formDirty: EventEmitter<boolean> = new EventEmitter<boolean>();
  private subject$: Subject<void>;

  faPlusCircle = faPlusCircle;
  faTimesCircle = faTimesCircle;
  devices: FormArray;
  parentForm: FormGroup;
  otherDevices;
  constructor(private siteDeviceService: SiteDeviceService,
              private snackBar: MatSnackBar) { }

  ngOnChanges(changes: SimpleChanges) {
    this.subject$ = new Subject<void>();
    this.setData();
  }

  private setData() {
    this.otherDevices = [];
    this.devices = new FormArray([]);
    if (this.site) {
      this.siteDeviceService.getDevices(this.site.id).subscribe(devices => {
        if (devices) {
          for (const device of devices) {
            if (device.config.type == FUELCELL) {
              this.otherDevices.push(device);
            }
          }

          for (const otherDevice of this.otherDevices) {
            const device = new FormGroup({
              id: new FormControl(otherDevice.store.id),
              deviceName: new FormControl(otherDevice.store.name),
            });
            this.devices.push(device);
          }
        } else {
          this.addBlankDevice();
        }
        this.parentForm = new FormGroup({
          devices: this.devices
        });
      });
    }
  }

  ngOnDestroy(): void {
    this.subject$.next();
    this.subject$.complete();
  }

  addBlankDevice(): void {
    const blankDevice =
      new FormGroup({
        id: new FormControl(''),
        deviceName: new FormControl('New other device', Validators.required),
      });

    this.otherDevices.push({
      store: { name: 'New other device'},
      config: {type: FUELCELL}
    });

    this.devices.push(blankDevice);
  }

  save(): void {
    if(this.site) {
      if(window.confirm(`Are you sure you want to save other devices for ${this.site.name}?`)){
        this.translateDeviceForm();
        const currentDevices = [];
        const newDevices = [];
        for (const otherDevice of this.otherDevices) {
          if (otherDevice.store.id != undefined) {
            currentDevices.push(otherDevice);
          } else {
            newDevices.push(otherDevice);
          }
        }

        let subscriptions = [];
        if (currentDevices.length) {
          subscriptions.push(this.siteDeviceService.putDevices(this.site.id, currentDevices));
        }

        if (newDevices.length) {
          subscriptions.push(this.siteDeviceService.postDevices(this.site.id, newDevices));
        }

        if (subscriptions.length) {
          forkJoin(subscriptions).subscribe(() => {
            this.formDirty.emit(false);
            this.setData();
            this.snackBar.open(`Other devices have been saved for ${this.site.name}.`, "Close",{
              duration: 3000,
              horizontalPosition: "right",
              verticalPosition: "bottom"
            });
          }, (error) => {
            this.snackBar.open(`Problem saving other devices for ${this.site.name}.`, "Close",{
              duration: 3000,
              horizontalPosition: "right",
              verticalPosition: "bottom"
            });
            console.log(`Problem saving other devices reason: ${error}`);
          })
        }
      }
    }
  }

  cancel(): void {
    this.parentForm.reset();
    this.formDirty.emit(false);
    this.setData();
  }

  removeDevice(device): void {
    if (window.confirm(`Are you sure you want to remove this device?`)) {
      for (const deviceIdx in this.otherDevices) {
        if (deviceIdx == device) {
          if (this.otherDevices[deviceIdx].store.id == undefined) {
            this.otherDevices.splice(deviceIdx,1);
            this.devices.removeAt(device);
            this.snackBar.open(`Device has been deleted.`, "Close",{
              duration: 3000,
              horizontalPosition: "right",
              verticalPosition: "bottom"
            });
            break;
          } else {
            this.siteDeviceService.deleteDevice(this.otherDevices[deviceIdx].store.id).subscribe(() => {
              this.snackBar.open(`Other device has been deleted.`, "Close",{
                duration: 3000,
                horizontalPosition: "right",
                verticalPosition: "bottom"
              });
              this.devices.removeAt(device);
              this.otherDevices.splice(deviceIdx, 1);
              this.formDirty.emit(false);
            }, (err) => {
              this.snackBar.open(`Problem deleting device.`, "Close",{
                duration: 3000,
                horizontalPosition: "right",
                verticalPosition: "bottom"
              });
              console.error(`Problem deleting device reason: ${err}`);
            });
          }
        }
      }
    }
  }

  setDirtyForm(): void {
    this.formDirty.emit(true)
  }

  private translateDeviceForm(): void {
    let otherDevices: Device[] = [];
    for (const deviceForm of this.devices.value) {
      let otherDevice = {
        store: {
          name: deviceForm.deviceName,
        },
        config: {
          type: FUELCELL
        }
      };

      if (deviceForm.id != '') {
        otherDevice.store['id'] = deviceForm.id;
        otherDevice.store['siteId'] = this.site.id;
      }

      otherDevices.push(otherDevice);
    }

    this.otherDevices = otherDevices;
  }
}
